import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { QRCodeSVG, QRCodeCanvas } from "qrcode.react";
import jsPDF from "jspdf";
import {
  IconArrowLeft, IconAward, IconCheck, IconPlus, IconTrash, IconExternalLink,
  IconShieldCheck, IconClock, IconBriefcase, IconLoader2, IconDownload, IconCopy,
  IconBook, IconPencil, IconShoppingCart, IconLock,
} from "@tabler/icons-react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import SombraCoursesAdmin from "@/pages/app/sello/SombraCoursesAdmin";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal";

export default function SelloRouter() {
  const { user } = useAuth();
  if (!user) return null;
  if (user.role === "psicologa" || user.is_admin) return <SelloAdmin />;
  if (user.role === "sombra") return <SelloSombra />;
  const canPost = user.role === "escuela" || user.role === "familia";
  return <SelloPool canPost={canPost} />;
}

// ---------- Sombra view ----------
function SelloSombra() {
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [steps, setSteps] = useState([]);
  const [app, setApp] = useState(null);
  const [tab, setTab] = useState("cert");
  const [form, setForm] = useState({ bio: "", years_experience: 0, specialties: "" });
  const [error, setError] = useState("");
  const load = async () => {
    const [{ data: s }, { data: a }] = await Promise.all([
      api.get("/sello/steps"),
      api.get("/sello/application/mine"),
    ]);
    setSteps(s.steps); setApp(a);
    if (a) setForm({ bio: a.bio || "", years_experience: a.years_experience || 0, specialties: (a.specialties || []).join(", ") });
  };
  useEffect(() => { load(); }, []);
  const create = async () => {
    setError("");
    try {
      await api.post("/sello/application", {
        bio: form.bio, years_experience: Number(form.years_experience),
        specialties: form.specialties.split(",").map((s) => s.trim()).filter(Boolean),
      });
      await load();
    } catch (e) { setError(formatApiError(e)); }
  };
  const saveEdit = async () => {
    setError("");
    try {
      await api.patch("/sello/application/mine", {
        bio: form.bio, years_experience: Number(form.years_experience),
        specialties: form.specialties.split(",").map((s) => s.trim()).filter(Boolean),
      });
      await load();
    } catch (e) { setError(formatApiError(e)); }
  };
  const toggle = async (stepId, done) => {
    await api.post("/sello/application/step", { step_id: stepId, done });
    await load();
  };
  const completedSet = new Set(app?.steps_completed || []);
  const pct = steps.length ? Math.round(completedSet.size / steps.length * 100) : 0;

  return (
    <>
      <SelloTop title={lang === "es" ? "Sello Sombra · Tu certificación" : "Sello Sombra · Your certification"} />
      <div className="wrap-app max-w-[860px] py-10">
        <div className="border-b border-line flex gap-1 mb-6">
          <button className={`px-3 py-2 text-[12.5px] font-medium border-b-2 -mb-px ${tab === "cert" ? "border-teal text-teal-deep" : "border-transparent text-muted"}`} onClick={() => setTab("cert")} data-testid="sello-tab-cert">{lang === "es" ? "Certificación" : "Certification"}</button>
          <button className={`px-3 py-2 text-[12.5px] font-medium border-b-2 -mb-px ${tab === "bolsa" ? "border-teal text-teal-deep" : "border-transparent text-muted"}`} onClick={() => setTab("bolsa")} data-testid="sello-tab-bolsa">{lang === "es" ? "Pool de colocación" : "Placement pool"}</button>
        </div>

        {tab === "cert" && (
          <>
            {!app ? (
              <div className="card space-y-3">
                <p className="lbl m-0">{lang === "es" ? "Comienza tu aplicación" : "Start your application"}</p>
                <div className="field"><label>Bio</label><textarea className="textarea" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} data-testid="app-bio" /></div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="field"><label>{lang === "es" ? "Años de experiencia" : "Years of experience"}</label><input type="number" className="input" value={form.years_experience} onChange={(e) => setForm({ ...form, years_experience: e.target.value })} /></div>
                  <div className="field"><label>{lang === "es" ? "Especialidades (coma)" : "Specialties (comma)"}</label><input className="input" placeholder="TDAH, TEA, regulación" value={form.specialties} onChange={(e) => setForm({ ...form, specialties: e.target.value })} /></div>
                </div>
                {error && <p className="text-errtext text-[13px]">{error}</p>}
                <button className="btn" onClick={create} data-testid="app-create">
                  <IconCheck size={13} /> {lang === "es" ? "Crear aplicación" : "Create"}
                </button>
              </div>
            ) : app.status === "certified" ? (
              <SelloCard app={app} />
            ) : (
              <>
                <div className="card">
                  <p className="lbl m-0 mb-3">{lang === "es" ? "Progreso" : "Progress"}</p>
                  <div className="track"><div className="fill" style={{ width: `${pct}%` }} /></div>
                  <p className="text-teal text-[12.5px] mt-1">{completedSet.size}/{steps.length} {lang === "es" ? "pasos" : "steps"} · {pct}%</p>
                  {app.status === "pending_review" && (
                    <div className="mt-3 pt-3 border-t border-line text-teal-deep text-[13px] flex items-center gap-2">
                      <IconClock size={14} /> {lang === "es" ? "Aplicación en revisión por Mariela." : "Under review."}
                    </div>
                  )}
                </div>
                <div className="space-y-2 mt-4">
                  {steps.map((s, i) => {
                    const done = completedSet.has(s.id);
                    return (
                      <div key={s.id} className="card flex items-start gap-3" data-testid={`step-${s.id}`}>
                        <button onClick={() => toggle(s.id, !done)} className={`mt-1 w-8 h-8 rounded-full flex items-center justify-center ${done ? "bg-oktext text-white" : "bg-teal-soft text-teal-deep"}`} data-testid={`step-toggle-${s.id}`}>
                          {done ? <IconCheck size={14} /> : <span className="text-[11px] font-medium">{String(i + 1).padStart(2, "0")}</span>}
                        </button>
                        <div className="flex-1">
                          <p className="font-medium text-[14px]">{s.title}</p>
                          <p className="text-muted text-[12.5px] mt-1">{s.detail}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="card mt-6 space-y-3">
                  <p className="lbl m-0">{lang === "es" ? "Editar perfil" : "Edit profile"}</p>
                  <textarea className="textarea" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} />
                  <div className="grid sm:grid-cols-2 gap-3">
                    <input type="number" className="input" value={form.years_experience} onChange={(e) => setForm({ ...form, years_experience: e.target.value })} />
                    <input className="input" value={form.specialties} onChange={(e) => setForm({ ...form, specialties: e.target.value })} />
                  </div>
                  <button className="btn-ghost btn" onClick={saveEdit}><IconCheck size={13} /> {lang === "es" ? "Guardar" : "Save"}</button>
                </div>
              </>
            )}
          </>
        )}

        {tab === "bolsa" && <PoolList canApply />}
      </div>
    </>
  );
}

// ---------- Sello card + QR ----------
function SelloCard({ app }) {
  const { lang } = useI18n();
  const url = `${window.location.origin}/verificar/${app.verification_code}`;
  const embedUrl = `${window.location.origin}/embed/sello/${app.verification_code}`;
  const embedSnippet = `<iframe src="${embedUrl}" width="440" height="140" frameborder="0" style="border:0;background:transparent;max-width:100%" title="Sello Sombra · Chiessa Institute"></iframe>`;
  const [copied, setCopied] = useState(false);
  const qrRef = useRef(null);

  const copyEmbed = async () => {
    try {
      await navigator.clipboard.writeText(embedSnippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* noop */ }
  };

  const downloadPdf = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    const qrDataUrl = canvas ? canvas.toDataURL("image/png") : null;
    const pdf = new jsPDF({ unit: "pt", format: "a4" });
    const W = pdf.internal.pageSize.getWidth();
    const H = pdf.internal.pageSize.getHeight();
    const teal = [10, 63, 51];
    const honey = [201, 145, 72];
    const ink = [24, 32, 30];
    const muted = [90, 100, 96];

    // Outer frame
    pdf.setDrawColor(...teal).setLineWidth(1.2).rect(28, 28, W - 56, H - 56);
    pdf.setDrawColor(...honey).setLineWidth(0.4).rect(38, 38, W - 76, H - 76);

    // Header band
    pdf.setFillColor(...teal).rect(28, 28, W - 56, 68, "F");
    pdf.setTextColor(255, 255, 255);
    pdf.setFont("times", "italic").setFontSize(22).text("Chiessa Institute", 56, 68);
    pdf.setFont("helvetica", "normal").setFontSize(9).text("NEUROEDUCACIÓN INCLUSIVA", 56, 84);

    // Honey tag
    pdf.setFillColor(...honey).roundedRect(W - 200, 46, 150, 30, 4, 4, "F");
    pdf.setTextColor(255, 255, 255).setFont("helvetica", "bold").setFontSize(10)
      .text(lang === "es" ? "SELLO VIGENTE" : "ACTIVE SEAL", W - 125, 65, { align: "center" });

    // Title
    pdf.setTextColor(...ink).setFont("times", "normal").setFontSize(28)
      .text(lang === "es" ? "Certificación Sello Sombra" : "Sello Sombra Certification", 56, 140);
    pdf.setDrawColor(...honey).setLineWidth(0.8).line(56, 152, 220, 152);

    // Name
    pdf.setTextColor(...teal).setFont("times", "italic").setFontSize(34).text(app.name || "—", 56, 200);

    // Meta line
    const issued = app.issued_at ? new Date(app.issued_at).toLocaleDateString(lang === "es" ? "es-HN" : "en-US") : "—";
    const expires = app.expires_at ? new Date(app.expires_at).toLocaleDateString(lang === "es" ? "es-HN" : "en-US") : "—";
    pdf.setTextColor(...muted).setFont("helvetica", "normal").setFontSize(10.5)
      .text(`${lang === "es" ? "Certificada el" : "Certified on"} ${issued}  ·  ${lang === "es" ? "vigente hasta" : "valid until"} ${expires}`, 56, 224);

    // Bio
    if (app.bio) {
      pdf.setTextColor(...ink).setFont("helvetica", "normal").setFontSize(11);
      const bioLines = pdf.splitTextToSize(app.bio, W - 260);
      pdf.text(bioLines, 56, 258);
    }

    // Specialties label
    let y = 340;
    pdf.setTextColor(...muted).setFont("helvetica", "bold").setFontSize(9)
      .text((lang === "es" ? "ESPECIALIDADES" : "SPECIALTIES").toUpperCase(), 56, y);
    pdf.setTextColor(...ink).setFont("helvetica", "normal").setFontSize(12)
      .text((app.specialties || []).join(" · ") || "—", 56, y + 18);

    // Years experience
    y += 54;
    pdf.setTextColor(...muted).setFont("helvetica", "bold").setFontSize(9)
      .text(lang === "es" ? "EXPERIENCIA" : "EXPERIENCE", 56, y);
    pdf.setTextColor(...ink).setFont("helvetica", "normal").setFontSize(12)
      .text(`${app.years_experience || 0} ${lang === "es" ? "años" : "years"}`, 56, y + 18);

    // QR + code (right column)
    if (qrDataUrl) {
      pdf.addImage(qrDataUrl, "PNG", W - 220, 250, 150, 150);
    }
    pdf.setTextColor(...muted).setFont("helvetica", "normal").setFontSize(8)
      .text(lang === "es" ? "Escanea para verificar" : "Scan to verify", W - 145, 412, { align: "center" });

    // Verification code block (bottom)
    pdf.setDrawColor(...teal).setLineWidth(0.4);
    pdf.line(56, H - 180, W - 56, H - 180);
    pdf.setTextColor(...muted).setFont("helvetica", "bold").setFontSize(9)
      .text(lang === "es" ? "CÓDIGO DE VERIFICACIÓN" : "VERIFICATION CODE", 56, H - 155);
    pdf.setTextColor(...teal).setFont("courier", "bold").setFontSize(22)
      .text(app.verification_code || "—", 56, H - 128);

    pdf.setTextColor(...muted).setFont("helvetica", "normal").setFontSize(9)
      .text(url, 56, H - 100);

    // Signature
    pdf.setDrawColor(...ink).setLineWidth(0.4).line(W - 260, H - 110, W - 56, H - 110);
    pdf.setTextColor(...ink).setFont("times", "italic").setFontSize(11)
      .text("Dra. Mariela Chiessa", W - 158, H - 122, { align: "center" });
    pdf.setTextColor(...muted).setFont("helvetica", "normal").setFontSize(8)
      .text(lang === "es" ? "Psicóloga · Directora" : "Psychologist · Director", W - 158, H - 96, { align: "center" });

    // Footer
    pdf.setTextColor(...muted).setFont("helvetica", "normal").setFontSize(7.5)
      .text(lang === "es"
        ? "Este sello acredita a la portadora como Maestra Sombra certificada por Chiessa Institute · Verificable en línea."
        : "This seal certifies the bearer as a Chiessa Institute Sello Sombra Teacher · Verifiable online.",
        W / 2, H - 44, { align: "center" });

    pdf.save(`sello-${app.verification_code || "chiessa"}.pdf`);
  };

  return (
    <div className="card border-l-[4px] border-l-honey" data-testid="sello-card">
      {/* Hidden canvas source for jsPDF embedding */}
      <div ref={qrRef} style={{ position: "absolute", left: -9999, top: -9999 }} aria-hidden>
        <QRCodeCanvas value={url} size={320} bgColor="#ffffff" fgColor="#0A3F33" level="M" />
      </div>
      <div className="flex items-start gap-6 flex-wrap">
        <div className="flex-1">
          <span className="tag tag-honey">{lang === "es" ? "Sello vigente" : "Active seal"}</span>
          <h2 className="font-display text-[26px] font-medium mt-2">{app.name}</h2>
          <p className="text-tert text-[13px] mt-1">
            {lang === "es" ? "Certificada el" : "Certified on"} {new Date(app.issued_at).toLocaleDateString(lang === "es" ? "es-HN" : "en-US")} · {lang === "es" ? "vigente hasta" : "valid until"} {new Date(app.expires_at).toLocaleDateString()}
          </p>
          <p className="text-[14px] mt-3">{app.bio}</p>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {(app.specialties || []).map((s) => <span key={s} className="tag">{s}</span>)}
          </div>
          <p className="lbl mt-6">{lang === "es" ? "Código de verificación" : "Verification code"}</p>
          <p className="font-mono text-[20px] font-medium text-teal-deep">{app.verification_code}</p>
          <div className="flex flex-wrap gap-2 mt-4">
            <a href={url} target="_blank" rel="noreferrer" className="btn-ghost btn inline-flex text-[12px]" data-testid="sello-public-link">
              <IconExternalLink size={12} /> {lang === "es" ? "Ver perfil público" : "Public profile"}
            </a>
            <button onClick={downloadPdf} className="btn text-[12px]" data-testid="sello-download-pdf">
              <IconDownload size={12} /> {lang === "es" ? "Descargar PDF" : "Download PDF"}
            </button>
          </div>
          {/* Embed badge snippet */}
          <div className="mt-6 pt-4 border-t border-line">
            <p className="lbl m-0 mb-2">{lang === "es" ? "Badge incrustable" : "Embeddable badge"}</p>
            <p className="text-muted text-[12.5px] mb-2 leading-relaxed">
              {lang === "es"
                ? "Pega este snippet en tu Instagram bio (link) o sitio personal. Al hacer clic redirige a la verificación pública."
                : "Paste this snippet on your Instagram bio (link) or personal site. Clicking redirects to public verification."}
            </p>
            <textarea
              className="textarea font-mono text-[11px] leading-snug"
              rows="3"
              readOnly
              value={embedSnippet}
              onFocus={(e) => e.target.select()}
              data-testid="sello-embed-snippet"
            />
            <div className="flex gap-2 mt-2">
              <button onClick={copyEmbed} className="btn-ghost btn text-[11.5px]" data-testid="sello-embed-copy">
                {copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
                {copied ? (lang === "es" ? "Copiado" : "Copied") : (lang === "es" ? "Copiar snippet" : "Copy snippet")}
              </button>
              <a href={embedUrl} target="_blank" rel="noreferrer" className="btn-ghost btn text-[11.5px]" data-testid="sello-embed-preview">
                <IconExternalLink size={12} /> {lang === "es" ? "Previsualizar badge" : "Preview badge"}
              </a>
            </div>
          </div>
        </div>
        <div className="text-center">
          <div className="bg-white p-3 rounded-xl border border-line">
            <QRCodeSVG value={url} size={150} bgColor="#ffffff" fgColor="#0A3F33" level="M" />
          </div>
          <p className="text-tert text-[11px] mt-2 font-mono uppercase tracking-widest">{lang === "es" ? "Escanea para verificar" : "Scan to verify"}</p>
        </div>
      </div>
    </div>
  );
}

// ---------- Admin view ----------
const CERT_STEPS = [
  { id: "m1_fundamentos", title_es: "Módulo 1 · Fundamentos del neurodesarrollo (14h)" },
  { id: "m2_perfiles", title_es: "Módulo 2 · Perfiles en el aula (16h)" },
  { id: "m3_rol", title_es: "Módulo 3 · Rol de la sombra: alcance y límites (14h)" },
  { id: "m4_estrategias", title_es: "Módulo 4 · Estrategias y adaptaciones DUA (16h)" },
  { id: "m5_conducta", title_es: "Módulo 5 · Conducta y co-regulación (14h)" },
  { id: "m6_equipo", title_es: "Módulo 6 · Equipo alrededor del niño (12h)" },
  { id: "m7_etica", title_es: "Módulo 7 · Ética, derechos y marco legal (10h)" },
  { id: "proyecto", title_es: "Proyecto final integrador (12h)" },
];
const TOTAL_STEPS = CERT_STEPS.length; // 8

function SelloAdmin() {
  const { lang } = useI18n();
  const nav = useNavigate();
  const [tab, setTab] = useState("solicitudes");
  const [items, setItems] = useState([]);
  const [decisionLoading, setDecisionLoading] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const load = async () => { const { data } = await api.get("/sello/applications"); setItems(data); };
  useEffect(() => { if (tab === "solicitudes") load(); }, [tab]);
  const review = async (id, decision) => {
    setDecisionLoading(id);
    try { await api.patch(`/sello/applications/${id}/review`, { decision }); await load(); }
    finally { setDecisionLoading(null); }
  };
  const doDelete = async () => {
    await api.delete(`/sello/applications/${deleteTarget.id}`);
    setDeleteTarget(null);
    await load();
  };
  const fmt = (iso) => iso ? new Date(iso).toLocaleString(lang === "es" ? "es-HN" : "en-US", { dateStyle: "medium", timeStyle: "short" }) : "—";
  return (
    <>
      <SelloTop title={lang === "es" ? "Sello Sombra · Gestión" : "Sello Sombra · Management"} />
      <div className="wrap-app max-w-[1080px] py-10">
        <div className="border-b border-line flex gap-1 mb-6 overflow-x-auto">
          <button
            className={`px-3 py-2 text-[12.5px] font-medium border-b-2 -mb-px flex items-center gap-1.5 ${tab === "solicitudes" ? "border-teal text-teal-deep" : "border-transparent text-muted"}`}
            onClick={() => setTab("solicitudes")}
            data-testid="sello-admin-tab-solicitudes"
          >
            <IconShieldCheck size={13} /> {lang === "es" ? "Solicitudes" : "Applications"}
          </button>
          <button
            className={`px-3 py-2 text-[12.5px] font-medium border-b-2 -mb-px flex items-center gap-1.5 ${tab === "cursos" ? "border-teal text-teal-deep" : "border-transparent text-muted"}`}
            onClick={() => setTab("cursos")}
            data-testid="sello-admin-tab-cursos"
          >
            <IconBook size={13} /> {lang === "es" ? "Cursos Sombra" : "Sombra Courses"}
          </button>
        </div>

        {tab === "cursos" && <SombraCoursesAdmin />}
        {tab === "solicitudes" && (
      <div className="space-y-3">
        {items.length === 0 && <p className="text-tert text-[13px]">{lang === "es" ? "Sin aplicaciones." : "No applications."}</p>}
        {items.map((a) => {
          const isOpen = expandedId === a.id;
          const doneSet = new Set(a.steps_completed || []);
          return (
            <div key={a.id} className="card" data-testid={`app-${a.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <p className="font-display text-[17px] font-medium">{a.name}</p>
                  <p className="text-tert text-[12px]">{a.email} · {a.years_experience} años · {(a.specialties || []).join(", ")}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`tag ${a.status === "certified" ? "bg-okbg text-oktext" : a.status === "pending_review" ? "bg-warnbg text-warntext" : a.status === "rejected" ? "bg-errbg text-errtext" : "bg-teal-soft text-teal-deep"}`}>{a.status}</span>
                  {a.status === "pending_review" && (
                    <>
                      <button className="btn text-[11px]" onClick={() => review(a.id, "certified")} disabled={decisionLoading === a.id} data-testid={`review-certify-${a.id}`}>
                        {decisionLoading === a.id ? <IconLoader2 size={12} className="animate-spin" /> : <IconAward size={12} />} {lang === "es" ? "Certificar" : "Certify"}
                      </button>
                      <button className="btn-ghost btn text-[11px]" onClick={() => review(a.id, "rejected")} data-testid={`review-reject-${a.id}`}>
                        {lang === "es" ? "Rechazar" : "Reject"}
                      </button>
                    </>
                  )}
                  <button
                    className="btn-ghost btn text-[11px]"
                    onClick={() => setExpandedId(isOpen ? null : a.id)}
                    data-testid={`review-detail-${a.id}`}
                  >
                    {isOpen ? (lang === "es" ? "Ocultar" : "Hide") : (lang === "es" ? "Ver detalle" : "Details")}
                  </button>
                  <button
                    className="btn-ghost btn text-[11px] text-errtext"
                    onClick={() => setDeleteTarget(a)}
                    data-testid={`app-delete-${a.id}`}
                    title={lang === "es" ? "Eliminar solicitud" : "Delete application"}
                  >
                    <IconTrash size={12} />
                  </button>
                </div>
              </div>
              {a.bio && <p className="text-[13.5px] mt-2 text-muted italic">{a.bio}</p>}
              <p className="text-tert text-[12px] mt-2">
                {a.steps_completed?.length || 0}/{TOTAL_STEPS} {lang === "es" ? "pasos" : "steps"}
                {a.status === "certified" && a.verification_code && (
                  <> · {lang === "es" ? "Código" : "Code"}: <span className="font-mono">{a.verification_code}</span></>
                )}
              </p>

              {isOpen && (
                <div className="mt-4 pt-4 border-t border-line" data-testid={`review-panel-${a.id}`}>
                  <p className="lbl m-0 mb-2">{lang === "es" ? "Pasos" : "Steps"}</p>
                  <div className="space-y-1.5 mb-4">
                    {CERT_STEPS.map((s) => (
                      <div key={s.id} className="flex items-center gap-2 text-[13px]">
                        <span className={`w-4 h-4 rounded-full flex items-center justify-center ${doneSet.has(s.id) ? "bg-oktext text-white" : "bg-line text-tert"}`}>
                          {doneSet.has(s.id) && <IconCheck size={10} />}
                        </span>
                        <span className={doneSet.has(s.id) ? "text-ink" : "text-tert line-through decoration-tert/40"}>{s.title_es}</span>
                      </div>
                    ))}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-[12.5px]">
                    <div><span className="text-tert">{lang === "es" ? "Creada" : "Created"}:</span> {fmt(a.created_at)}</div>
                    <div><span className="text-tert">{lang === "es" ? "Última actualización" : "Last update"}:</span> {fmt(a.updated_at)}</div>
                    <div><span className="text-tert">{lang === "es" ? "Revisada" : "Reviewed"}:</span> {fmt(a.reviewed_at)}</div>
                    <div><span className="text-tert">{lang === "es" ? "Emitida" : "Issued"}:</span> {fmt(a.issued_at)}</div>
                    <div><span className="text-tert">{lang === "es" ? "Vence" : "Expires"}:</span> {fmt(a.expires_at)}</div>
                    <div><span className="text-tert">{lang === "es" ? "Años exp." : "Years exp."}:</span> {a.years_experience || "—"}</div>
                  </div>
                  {a.status === "certified" && a.verification_code && (
                    <div className="mt-4 p-3 bg-teal-soft rounded-lg">
                      <p className="lbl m-0">{lang === "es" ? "Perfil público" : "Public profile"}</p>
                      <a
                        href={`${window.location.origin}/verificar/${a.verification_code}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-teal-deep text-[13px] underline underline-offset-2"
                      >
                        /verificar/{a.verification_code}
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
        )}
      </div>
      <ConfirmDeleteModal
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={doDelete}
        title={lang === "es" ? "Eliminar solicitud de Sello Sombra" : "Delete Sello Sombra application"}
        description={
          deleteTarget
            ? (lang === "es"
                ? `Se eliminará la solicitud de <b>${deleteTarget.name}</b> (${deleteTarget.email}). Esta acción es irreversible.`
                : `Application from <b>${deleteTarget.name}</b> (${deleteTarget.email}) will be removed. This action is irreversible.`)
            : ""
        }
        testId="sello-app-delete-modal"
      />
    </>
  );
}

// ---------- Pool (for escuela/familia + sombra tab) ----------
function SelloPool({ canPost = false }) {
  const { lang } = useI18n();
  return (
    <>
      <SelloTop title={lang === "es" ? "Sello Sombra · Bolsa" : "Sello Sombra · Pool"} />
      <div className="wrap-app max-w-[860px] py-10">
        <PoolList canPost={canPost} />
      </div>
    </>
  );
}

function PoolList({ canApply = false, canPost = false }) {
  const { lang } = useI18n();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", kind: "familia", city: "", grade: "", condition: "", notes: "" });
  const [message, setMessage] = useState({});
  const [applied, setApplied] = useState({});
  const load = async () => { const { data } = await api.get("/sello/pool"); setItems(data); };
  useEffect(() => { load(); }, []);
  const post = async () => {
    if (!form.title) return;
    await api.post("/sello/pool", form);
    setForm({ title: "", kind: "familia", city: "", grade: "", condition: "", notes: "" });
    setShowForm(false); await load();
  };
  const apply = async (pid) => {
    try {
      await api.post("/sello/pool/interest", { placement_id: pid, message: message[pid] || "" });
      setApplied({ ...applied, [pid]: true });
    } catch { /* already applied etc. */ }
  };
  return (
    <div className="space-y-4">
      {(canPost) && (
        <div className="flex justify-end">
          <button className="btn text-[12px]" onClick={() => setShowForm((v) => !v)} data-testid="pool-new">
            <IconPlus size={13} /> {lang === "es" ? "Publicar espacio" : "New post"}
          </button>
        </div>
      )}
      {showForm && (
        <div className="card">
          <div className="grid sm:grid-cols-2 gap-3">
            <input className="input" placeholder={lang === "es" ? "Título" : "Title"} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="pool-title" />
            <select className="select-css" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
              <option value="familia">Familia</option><option value="escuela">Escuela</option>
            </select>
            <input className="input" placeholder="Ciudad" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            <input className="input" placeholder="Grado" value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} />
            <input className="input sm:col-span-2" placeholder="Condición" value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} />
            <textarea className="textarea sm:col-span-2" placeholder="Notas" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <button className="btn mt-2" onClick={post} data-testid="pool-submit"><IconCheck size={13} /> {lang === "es" ? "Publicar" : "Post"}</button>
        </div>
      )}
      {items.length === 0 && <p className="text-tert text-[13px]">{lang === "es" ? "Sin espacios abiertos." : "No open positions."}</p>}
      {items.map((p) => (
        <div key={p.id} className="card" data-testid={`pool-${p.id}`}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1">
              <span className="tag">{p.kind}</span>
              <h3 className="font-display text-[17px] font-medium mt-1">{p.title}</h3>
              <p className="text-tert text-[12.5px] mt-0.5">{p.city || "—"} · {p.grade || "—"} · {p.condition || "—"}</p>
              {p.notes && <p className="text-[13.5px] mt-2 leading-relaxed">{p.notes}</p>}
              <p className="text-tert text-[11.5px] mt-2">{p.poster_name}</p>
            </div>
            {canApply && (
              <div className="w-full sm:w-auto">
                {applied[p.id] ? (
                  <span className="tag bg-okbg text-oktext">{lang === "es" ? "Postulada" : "Applied"}</span>
                ) : (
                  <>
                    <textarea className="textarea mb-2" rows="2" placeholder={lang === "es" ? "Mensaje breve..." : "Short message..."} value={message[p.id] || ""} onChange={(e) => setMessage({ ...message, [p.id]: e.target.value })} data-testid={`pool-msg-${p.id}`} />
                    <button className="btn text-[11px]" onClick={() => apply(p.id)} data-testid={`pool-apply-${p.id}`}>
                      <IconBriefcase size={12} /> {lang === "es" ? "Postularme" : "Apply"}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function SelloTop({ title }) {
  const { t } = useI18n();
  const nav = useNavigate();
  return (
    <div className="subbar">
      <button className="btn-ghost btn text-[12px] px-2 py-1" onClick={() => nav("/app")} data-testid="sello-back">
        <IconArrowLeft size={13} /> {t("app.back")}
      </button>
      <span className="t">{title}</span>
    </div>
  );
}
